import { useCallback } from 'react';
import { ResourceType } from '@cotera/api';
import { resourceArtifactData } from '@cotera/client/v0/chat/artifacts';
import {
  useArtifactPanel,
  type ArtifactPanelViewEntry,
} from '@cotera/client/v0/chat/artifact-panel-context';
import { useWorkflowModel } from './workflow.model';
import { workflowAgentViewEntry } from './workflow-artifact-views';

/**
 * Open a resource a workflow points at — the agent behind a node, the dataset a
 * trigger watches — from inside the workflow.
 *
 * A sibling tab wherever the panel has them: the resource sits *beside* the
 * workflow rather than inside it, one tab away and its own thing, which is what
 * it is. Buried on the workflow's view stack it read as part of the workflow's
 * configuration, where leaving it meant walking back out through the workflow
 * rather than simply looking elsewhere.
 *
 * The standalone editor pages' transient panel has no siblings to open into, so
 * there it stays a stack view — hence `fallbackEntry`, which is only ever built
 * for that case. `expand` goes with it: a stack view replaces the canvas the
 * inspector floats over, so it wants the main body; a sibling tab already has
 * the whole panel.
 */
export function useOpenWorkflowResource(): (params: {
  /** The kind of resource to open as a sibling tab. */
  resourceType: ResourceType;
  artifactId: string;
  /** Stack entry for a panel with no sibling tabs. */
  fallbackEntry: ArtifactPanelViewEntry;
}) => void {
  const artifactPanel = useArtifactPanel();
  return useCallback(
    ({ resourceType, artifactId, fallbackEntry }) => {
      if (artifactPanel === null || artifactId === '') {
        return;
      }
      if (artifactPanel.canOpenArtifacts) {
        // The payload key is the resource kind's business, not this hook's —
        // `resourceArtifactData` reads it off the same `resourceKind` registry
        // the rest of the app opens artifacts through, so `type` and the id it
        // carries cannot drift apart the way four hand-written pairs could.
        artifactPanel.openArtifact({
          type: resourceType,
          artifactId,
          payload: resourceArtifactData(resourceType, artifactId),
        });
        return;
      }
      artifactPanel.expand();
      artifactPanel.pushView(fallbackEntry, { replaceTop: true });
    },
    [artifactPanel]
  );
}

/**
 * {@link useOpenWorkflowResource} for the agent behind a node.
 *
 * Two editors open the same thing — an agent node's own agent, and the agent a
 * for-each node runs its loop body with — and each carried the same guard, the
 * same entry and the same call to get there. The workflow id comes from the
 * model, so a caller is left with the two things that actually differ.
 */
export function useOpenWorkflowAgent(): (
  agentId: string | undefined,
  agentName?: string
) => void {
  const openWorkflowResource = useOpenWorkflowResource();
  const model = useWorkflowModel();
  return useCallback(
    (agentId, agentName) => {
      if (agentId === undefined || agentId === '') {
        return;
      }
      openWorkflowResource({
        resourceType: ResourceType.Agent,
        artifactId: agentId,
        // A blank name and no name mean the same thing here; the entry
        // normalises both.
        fallbackEntry: workflowAgentViewEntry({
          workflowId: model.workflowId,
          agentId,
          agentName,
        }),
      });
    },
    [openWorkflowResource, model.workflowId]
  );
}
